import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {Tooltip, Flex, Image, Text} from '../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../utils/chakraRouterLink'

// Imported from the concrete module rather than the services/api barrel so a
// test that auto-mocks the barrel's transport doesn't also stub out this pure
// URL builder (it makes no request — it just composes a thumbnail URL from the
// shared http instance's baseURL). Mirrors FaceConfidenceModal's handling of
// buildTrainingPhotoImageUrl.
import {getThumbnail} from '../../../services/api/photos/getThumbnail'
import {buildAlbumPhotoUrl, isLinkableId} from '../../../utils/photoDeepLink'
import {PersonTaggedPhoto} from '../../../types'
import {formatPhotoLabel} from '../formatters'
import copy from '../copy.json'

// Matches the server's per-category cap (pb.PhotoSampleLimit). Applied here too
// because the close-call strips are built from an uncapped list and would
// otherwise render an unbounded row of images.
export const PHOTO_STRIP_LIMIT = 10

interface Props {
  photos: PersonTaggedPhoto[]
  clientId: number
  testId: string
  /**
   * Whether the album deep link can be trusted for this lookup. False when the
   * operator is inspecting a camp other than the one their session is scoped
   * to — see FrDiagnosticsView. Thumbnails still render; only the link is
   * withheld.
   */
  canLinkAlbums: boolean
}

/**
 * A row of up to PHOTO_STRIP_LIMIT thumbnails, each linking to that photo
 * inside its album.
 *
 * The link is the point: a photo id on its own is an internal primary key that
 * identifies nothing to a human, so without it a strip is just anonymous
 * pictures. The album route scrolls to and highlights the linked photo (see
 * useScrollToPhotoParam), which also puts the gallery's own FR actions within
 * reach.
 *
 * Renders nothing when there are no photos — the surrounding section already
 * has its own empty-state copy, so a second "no photos" line would be noise.
 */
export const PhotoStrip: React.FC<Props> = ({
  photos,
  clientId,
  testId,
  canLinkAlbums,
}): React.ReactElement | null => {
  // Track failures per photo id so one dead thumbnail doesn't blank the strip.
  const [failedIds, setFailedIds] = React.useState<Set<number>>(() => new Set())

  React.useEffect(() => {
    setFailedIds(new Set())
  }, [clientId])

  if (photos.length === 0) return null

  const shown = photos.slice(0, PHOTO_STRIP_LIMIT)

  return (
    <Flex gap={2} wrap='wrap' mt={2} data-testid={testId}>
      {shown.map((photo) => {
        const label = formatPhotoLabel(photo.albumName, photo.photoId)

        // Link only when the target is actually reachable: a non-positive
        // album id (a skewed or malformed payload, coerced to 0 at decode) or a
        // cross-camp lookup would both produce a link that navigates the
        // operator somewhere wrong. A wrong album silently showing a different
        // camp is worse on a diagnostics page than no link at all.
        const linkable = canLinkAlbums && isLinkableId(photo.albumId)

        const thumbnail = failedIds.has(photo.photoId) ? (
          <Flex
            w='64px'
            h='64px'
            bg='gray.100'
            borderRadius='md'
            align='center'
            justify='center'>
            <Text fontSize='xs' color='gray.700'>
              {copy.photos.thumbnailFailed}
            </Text>
          </Flex>
        ) : (
          <Image
            src={getThumbnail({
              clientID: String(clientId),
              ID: String(photo.photoId),
            })}
            alt={label}
            w='64px'
            h='64px'
            objectFit='cover'
            borderRadius='md'
            onError={(): void =>
              setFailedIds((prev) => new Set(prev).add(photo.photoId))
            }
          />
        )

        const tooltipLabel = linkable
          ? label
          : `${label} — ${copy.photos.notLinkable}`

        return (
          <Tooltip key={photo.photoId} label={tooltipLabel} openDelay={300}>
            {linkable ? (
              <Link
                as={ReactRouterLink}
                to={buildAlbumPhotoUrl(photo.albumId, photo.photoId)}
                aria-label={label}
                data-testid={`${testId}-link-${photo.photoId}`}
                _hover={{opacity: 0.8}}>
                {thumbnail}
              </Link>
            ) : (
              <Flex
                aria-label={tooltipLabel}
                data-testid={`${testId}-unlinked-${photo.photoId}`}>
                {thumbnail}
              </Flex>
            )}
          </Tooltip>
        )
      })}
    </Flex>
  )
}
